import {
  AbstractControl,
  FormGroup,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { translate } from '@jsverse/transloco';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { ErrorObjectUtils } from './error-object.utils';

export const noWhitespaceValidator = (
  control: UntypedFormControl
): { [key: string]: any } | null => {
  const isWhitespace = (control.value || '').trim().length === 0;
  const isValid = !isWhitespace;
  return isValid ? null : { whitespace: true };
};

export const passwordsMatchValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const password = control.get('password');
  const confirm = control.get('passwordConfirm');

  let ret;

  if (!password.value) {
    ret = { required: true };
    password.setErrors(ret);
  } else if (!confirm.value) {
    ret = { required: true };
    confirm.setErrors(ret);
  } else {
    ret = password.value === confirm.value ? null : { passwordsNotMatch: true };

    if (ret || password.hasError('required') || password.hasError('passwordsNotMatch')) {
      const { required, passwordsNotMatch, ...otherErrors } = password.errors || {};
      const existingErrors = { ...otherErrors, ...ret };
      password.setErrors(Object.keys(existingErrors).length ? existingErrors : null);
    }

    if (ret || confirm.hasError('required') || confirm.hasError('passwordsNotMatch')) {
      const { required, passwordsNotMatch, ...otherErrors } = confirm.errors || {};
      const existingErrors = { ...otherErrors, ...ret };
      confirm.setErrors(Object.keys(existingErrors).length ? existingErrors : null);
    }
  }

  return ret;
};

export function generalNameValidator(
  toValidate: string,
  errorObject: ErrorObject,
  errorTitle: string,
  maxLength: number,
  required: boolean,
  exsistingNames: string[],
  caseSensitive: boolean,
  forbiddenItems: { character: string; name: string }[] = [],
  expressionToMatch?: { regexp: RegExp; description: string }
): void {
  if (required && (!toValidate || !toValidate.trim())) {
    ErrorObjectUtils.setError(
      errorObject,
      translate('errors.item-name-is-required', { item: errorTitle })
    );
    return;
  }

  if (maxLength > 0 && toValidate.length > maxLength) {
    ErrorObjectUtils.setError(
      errorObject,
      translate('errors.item-name-max-length', { item: errorTitle, max: maxLength })
    );
    return;
  }

  const options: Intl.CollatorOptions = caseSensitive ? undefined : { sensitivity: 'accent' };

  if (exsistingNames.some((name) => !name.localeCompare(toValidate, undefined, options))) {
    ErrorObjectUtils.setError(
      errorObject,
      translate('errors.item-already-exists', { item: errorTitle })
    );
    return;
  }

  // From this point the errorTitle appears inside the error strings
  errorTitle = errorTitle.toLowerCase();

  for (let i = 0; i < forbiddenItems.length; i++) {
    const forbidden = forbiddenItems[i];
    if (toValidate.includes(forbidden.character)) {
      ErrorObjectUtils.setError(
        errorObject,
        translate('errors.item-name-contains-forbidden', { item: errorTitle, text: forbidden.name })
      );
      return;
    }
  }

  if (expressionToMatch && !toValidate.match(expressionToMatch.regexp)) {
    ErrorObjectUtils.setError(
      errorObject,
      translate('errors.item-name-conditions', {
        item: errorTitle,
        condition: expressionToMatch.description,
      })
    );
    return;
  }

  ErrorObjectUtils.setNoError(errorObject);
}

export async function generalAsyncNameValidator(
  toValidate: string,
  errorObject: ErrorObject,
  errorTitle: string,
  maxLength: number,
  required: boolean,
  validationApi: () => Promise<unknown>,
  originalName?: string,
  forbiddenItems: { character: string; name: string }[] = [],
  expressionToMatch?: { regexp: RegExp; description: string }
): Promise<ErrorObject> {
  generalNameValidator(
    toValidate,
    errorObject,
    errorTitle,
    maxLength,
    required,
    [],
    false,
    forbiddenItems,
    expressionToMatch
  );

  if (errorObject.state !== EnumErrorStates.SUCCESS) {
    return errorObject;
  }

  if (
    errorObject.state !== EnumErrorStates.SUCCESS &&
    originalName &&
    toValidate.localeCompare(originalName, undefined, { sensitivity: 'accent' }) === 0
  ) {
    return errorObject;
  }

  return validationApi()
    .then((res) => {
      if (res) {
        const text = translate('errors.item-already-exists', { item: errorTitle });
        return ErrorObjectUtils.setError(errorObject, text);
      }
    })
    .catch((err) => {
      if (err.status === 404) {
        return errorObject;
      }
    });
}

export function requireCheckboxesToBeCheckedValidator(minRequired = 1): ValidatorFn {
  return function validate(formGroup: FormGroup) {
    const checked = Object.values(formGroup.controls).filter((c) => c.value).length;

    if (checked < minRequired) {
      return {
        requireCheckboxesToBeChecked: true,
      };
    }

    return null;
  };
}

export function jsonValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const error: ValidationErrors = { jsonInvalid: true };

    try {
      JSON.parse(control.value);
    } catch (e) {
      control.setErrors(error);
      return error;
    }

    control.setErrors(null);
    return null;
  };
}

export function notAllCheckedValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const values = Object.values(control.value);

    const allChecked = values.every(Boolean);

    return allChecked ? { allCheckedNotAllowed: true } : null;
  };
}
